/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { forwardRef, useEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import ScrollLock from 'react-scrolllock';
import { mergeRefs } from 'use-callback-ref';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { type DrawerContentProps } from '../types';
import { useEnsureIsInsideDrawer } from '../use-ensure-is-inside-drawer';

import usePreventProgrammaticScroll from './hooks/use-prevent-programmatic-scroll';

const styles = cssMap({
	default: {
		flex: 1,
		overflow: 'auto',
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		marginTop: token('space.300'),
	},
});

const DrawerContentBase: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<DrawerContentProps> & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, DrawerContentProps>(
	({ children, scrollContentLabel = 'Scrollable content', xcss }, scrollableRef) => {
		const [showContentFocus, setContentFocus] = useState(false);
		const ref = useRef<HTMLDivElement>(null);

		useEffect(() => {
			const setLazyContentFocus = () => {
				const target = ref.current;
				target && setContentFocus(target.scrollHeight > target.clientHeight);
			};

			setLazyContentFocus();
		}, []);

		return (
			<div
				css={styles.default}
				className={xcss}
				ref={mergeRefs([ref, scrollableRef])}
				// tabindex is allowed here so that keyboard users can scroll content
				tabIndex={showContentFocus ? 0 : undefined}
				role={showContentFocus ? 'region' : undefined}
				aria-label={showContentFocus ? scrollContentLabel : undefined}
				data-testid="drawer-contents"
			>
				{children}
			</div>
		);
	},
);

/**
 * __Drawer content__
 *
 * The main content section of the drawer panel.
 */
export const DrawerContent: ({
	children,
	scrollContentLabel,
	xcss,
}: DrawerContentProps) => JSX.Element = ({
	children,
	scrollContentLabel,
	xcss,
}: DrawerContentProps) => {
	useEnsureIsInsideDrawer();
	usePreventProgrammaticScroll();

	const content = (
		<DrawerContentBase scrollContentLabel={scrollContentLabel} xcss={xcss}>
			{children}
		</DrawerContentBase>
	);

	// Under the top-layer flag, background scroll is handled by `DialogScrollLock`
	// (rendered by `DrawerTopLayer`), so we skip `react-scrolllock` here to avoid
	// double-locking the body. See the top-layer migration scroll decision.
	if (fg('platform-dst-top-layer')) {
		return content;
	}

	return (
		<ScrollLock>
			{/* An intermediate component is used to work around the behaviour of react-scrolllock
			overriding its child ref. Enabling us to merge the ref from react-scrolllock with our ref. */}
			{content}
		</ScrollLock>
	);
};
